import { Camera, ChevronRight, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { cleaningPreparationLabel } from '../CleaningPreparationSummary';
import { cleaningOccupancySummary } from '../cleaningOccupancy';
import { PLAN_PILL } from '../my-tasks/myTasksModel';
import { arrivalLabel, cleaningDayStatus } from './cleaningDayModel';

interface Props {
  task: CleaningTaskQueueResponse;
  date: string;
  selected: boolean;
  onSelect: (task: CleaningTaskQueueResponse, trigger: HTMLElement) => void;
  onClaim: (task: CleaningTaskQueueResponse) => void;
  claiming: boolean;
  offline?: boolean;
  currentUserId?: string;
}

function statusTone(task: CleaningTaskQueueResponse): string {
  if (task.isOverdue || task.status === 'Issue') return 'bg-sealwax-tint text-sealwax';
  if (task.status === 'Completed') return 'bg-forest-soft text-forest-deep';
  if (task.status === 'InProgress') return 'bg-amber-tint text-amber-ink';
  if (task.status === 'Pending' && !task.assignedToUserId) return 'bg-forest-soft text-forest-deep';
  return 'bg-card-alt text-ink';
}

/** Eine Aufgabe in der Tagesliste der Leitung; die ganze Zeile öffnet das Detailblatt. */
export function CleaningTaskRow({
  task,
  date,
  selected,
  onSelect,
  onClaim,
  claiming,
  offline = false,
  currentUserId,
}: Props) {
  const { t } = useTranslation();
  const status = cleaningDayStatus(task, currentUserId);
  return (
    <li className="border-hairline border-b last:border-b-0">
      <article className={cn('px-4 py-3 sm:px-5', selected && 'bg-forest-soft/50')}>
        <button
          type="button"
          aria-pressed={selected}
          aria-label={`${task.propertyName}, ${status}, Details öffnen`}
          onClick={(event) => onSelect(task, event.currentTarget)}
          className="grid min-h-11 w-full gap-2 text-left md:grid-cols-[minmax(10rem,1fr)_minmax(14rem,1.6fr)_10rem] md:items-center md:gap-4"
        >
          <span className="flex min-w-0 flex-col gap-1">
            <span className="flex min-w-0 items-center gap-2">
              <b className="truncate font-serif text-lg">{task.propertyName}</b>
              <span className={cn(PLAN_PILL, statusTone(task))}>{status}</span>
            </span>
            {task.propertyInternalName && task.propertyInternalName !== task.propertyName && (
              <span className="text-ink-soft truncate text-xs">
                Intern: {task.propertyInternalName}
              </span>
            )}
          </span>
          <span className="text-ink-soft flex min-w-0 flex-col gap-0.5 text-xs">
            <span className="text-ink text-sm tabular-nums">
              {task.scheduledFromLocalTime.slice(0, 5)}–
              {task.scheduledToLocalTime?.slice(0, 5) ?? 'offen'}
              {' · '}
              {arrivalLabel(task, date)}
            </span>
            <span className="break-words">{cleaningPreparationLabel(task.nextArrival, t)}</span>
            {task.nextArrival && <span>{cleaningOccupancySummary(task.nextArrival)}</span>}
          </span>
          <span className="flex items-center justify-between gap-3 md:justify-end">
            <span className="text-ink-soft flex flex-col text-xs md:items-end">
              <span>
                {task.assignedToUserName ??
                  (task.assignedToUserId ? 'Zuweisung prüfen' : 'Nicht zugewiesen')}
              </span>
              <span className="text-ink flex items-center gap-1 tabular-nums">
                <Camera size={14} aria-hidden />
                {task.photoSlotCount > 0
                  ? `${task.filledPhotoSlotCount} von ${task.photoSlotCount}`
                  : 'ohne Fotos'}
              </span>
            </span>
            <ChevronRight className="text-ink-faint" size={20} aria-hidden />
          </span>
        </button>
        {task.canClaim && (
          <Button
            variant="brand"
            size="lg"
            className="mt-3 w-full md:hidden"
            leftIcon={<Play size={18} aria-hidden />}
            loading={claiming}
            disabled={offline}
            onClick={() => onClaim(task)}
          >
            Übernehmen &amp; starten
          </Button>
        )}
      </article>
    </li>
  );
}
